import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useSocket } from '../context/SocketContext';

export default function Broadcast() {
  const { socket, waStatus } = useSocket();
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  
  // Form State
  const [name, setName] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [customMsg, setCustomMsg] = useState('');
  const [minDelay, setMinDelay] = useState(2);
  const [maxDelay, setMaxDelay] = useState(5);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);

  // Live Progress State
  const [activeBroadcast, setActiveBroadcast] = useState(null);
  const [logs, setLogs] = useState([]);
  const logEndRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
    fetchHistory();
  }, []);

  useEffect(() => {
    if (!socket) return;
    
    const handleUpdate = (data) => {
      // data: { broadcastId, sent, total, status, log }
      setActiveBroadcast(prev => {
        if (!prev || prev.id === data.broadcastId) {
          return { id: data.broadcastId, sent: data.sent, total: data.total, status: data.status };
        }
        return prev;
      });

      if (data.log) {
        setLogs(prev => [...prev, data.log]);
      }
      
      if (data.status === 'completed' || data.status === 'cancelled') {
        fetchHistory();
        setStopping(false);
      }
    };

    socket.on('broadcast_update', handleUpdate);
    return () => socket.off('broadcast_update', handleUpdate);
  }, [socket]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const fetchTemplates = async () => {
    try {
      const { data } = await api.get('/api/templates');
      setTemplates(data);
    } catch (e) { console.error('Error fetching templates:', e); }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await api.get('/api/broadcast');
      setHistory(data);
    } catch (e) { console.error('Error fetching history:', e); }
  };

  const handleStart = async (e) => {
    e.preventDefault();
    if (waStatus !== 'ready') return alert('WhatsApp is not ready. Please scan QR Code.');
    if (!csvFile) return alert('CSV file is required.');
    if (!templateId && !customMsg) return alert('Please select a template or enter a custom message.');

    if (!window.confirm('Are you ready to start this broadcast?')) return;

    const fd = new FormData();
    fd.append('name', name || 'Untitled Broadcast');
    fd.append('csvFile', csvFile);
    if (mediaFile) fd.append('mediaFile', mediaFile);
    if (templateId) fd.append('templateId', templateId);
    if (customMsg) fd.append('message', customMsg);
    fd.append('minDelay', minDelay);
    fd.append('maxDelay', maxDelay);

    setStarting(true);
    try {
      const { data } = await api.post('/api/broadcast', fd);
      setActiveBroadcast({ id: data.broadcastId, sent: 0, total: data.total, status: 'starting' });
      setLogs([{ type: 'info', number: 'System', reason: 'Broadcast queued and starting soon...' }]);
      
      // reset form
      setName('');
      setCsvFile(null);
      setMediaFile(null);
      setTemplateId('');
      setCustomMsg('');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to start broadcast');
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    if (!activeBroadcast?.id) return;
    setStopping(true);
    try {
      await api.post(`/api/broadcast/${activeBroadcast.id}/stop`);
      setLogs(prev => [...prev, { type: 'info', number: 'System', reason: 'Stop signal sent — halting after current message...' }]);
    } catch (err) {
      setStopping(false);
      alert(err.response?.data?.error || 'Failed to stop broadcast');
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Marketing Broadcast</h1>
        <p style={styles.subtitle}>Send bulk WhatsApp messages from uploaded CSV securely to avoid restrictions.</p>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {/* LEFT COLUMN: Config */}
        <div style={{ flex: '1 1 45%' }}>
          <form onSubmit={handleStart} style={styles.card}>
            <h3 style={styles.cardTitle}>Broadcast Setup</h3>
            
            <div style={styles.field}>
              <label style={styles.label}>Broadcast Name</label>
              <input type="text" style={styles.input} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Diwali Promo 2026" />
            </div>

            <div style={styles.field}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{...styles.label, marginBottom: 0}}>Audience List (CSV Format: phone, name)</label>
                <a 
                  href="data:text/csv;charset=utf-8,phone,name%0A919876543210,John%20Doe%0A919988776655,Jane%20Smith" 
                  download="sample_contacts.csv" 
                  style={{ fontSize: 11, color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}
                >
                  📄 Download Sample CSV
                </a>
              </div>
              <input type="file" accept=".csv" style={styles.fileInput} onChange={e => setCsvFile(e.target.files[0])} required />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Message Template</label>
              <select style={styles.input} value={templateId} onChange={e => { setTemplateId(e.target.value); setCustomMsg(''); }}>
                <option value="">-- Choose a pre-saved Template --</option>
                {templates.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
            </div>

            {!templateId && (
              <div style={styles.field}>
                <label style={styles.label}>Or Custom Message (Use {"{{"}name{"}}"} for recipient name)</label>
                 <textarea style={styles.textarea} rows={4} value={customMsg} onChange={e => setCustomMsg(e.target.value)} placeholder="Type custom message..."></textarea>
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>Media Attachment (Optional Image/Video)</label>
              <input type="file" accept="image/*,video/*,application/pdf" style={styles.fileInput} onChange={e => setMediaFile(e.target.files[0])} />
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <div style={styles.field}>
                <label style={styles.label}>Min Delay (s)</label>
                <input type="number" style={styles.input} value={minDelay} onChange={e => setMinDelay(Number(e.target.value))} min={1} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Max Delay (s)</label>
                <input type="number" style={styles.input} value={maxDelay} onChange={e => setMaxDelay(Number(e.target.value))} min={1} />
              </div>
            </div>

            <button type="submit" disabled={starting || waStatus !== 'ready' || (activeBroadcast && (activeBroadcast.status === 'sending' || activeBroadcast.status === 'starting'))} style={{...styles.startBtn, opacity: (starting || waStatus !== 'ready' || (activeBroadcast && (activeBroadcast.status === 'sending' || activeBroadcast.status === 'starting'))) ? 0.5 : 1}}>
              {starting ? 'Initializing...' : '🚀 Start Broadcast'}
            </button>
            {waStatus !== 'ready' && <p style={{ fontSize: 12, color: '#ef4444', marginTop: 8 }}>⚠️ Please scan WhatsApp QR in Bot Management before starting.</p>}
            {activeBroadcast && (activeBroadcast.status === 'sending' || activeBroadcast.status === 'starting') && (
              <p style={{ fontSize: 12, color: '#fb923c', marginTop: 8 }}>⚠️ A broadcast is already running. Please wait or stop it first.</p>
            )}
          </form>
        </div>

        {/* RIGHT COLUMN: Live View */}
        <div style={{ flex: '1 1 45%', display: 'flex', flexDirection: 'column', gap: 24 }}>
          {activeBroadcast && (
            <div style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={styles.cardTitle}>Live Progress</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    ...styles.badge,
                    background: activeBroadcast.status === 'cancelled' ? 'rgba(248,113,113,0.15)' : activeBroadcast.status === 'completed' ? 'rgba(52,211,153,0.15)' : 'rgba(56,189,248,0.12)',
                    color: activeBroadcast.status === 'cancelled' ? '#f87171' : activeBroadcast.status === 'completed' ? '#34d399' : '#38bdf8',
                  }}>
                    {activeBroadcast.status.toUpperCase()}
                  </span>
                  {activeBroadcast.status === 'sending' || activeBroadcast.status === 'starting' ? (
                    <button
                      onClick={handleStop}
                      disabled={stopping}
                      style={styles.stopBtn}
                    >
                      {stopping ? 'Stopping...' : 'Stop Broadcast'}
                    </button>
                  ) : null}
                </div>
              </div>
              
              <div style={styles.progressBg}>
                 <div style={{...styles.progressFill, width: `${(activeBroadcast.sent / activeBroadcast.total) * 100}%`}}></div>
              </div>
               <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 8, textAlign: 'right' }}>
                  {activeBroadcast.sent} / {activeBroadcast.total} Messages Sent
               </p>

               <div style={styles.terminal}>
                  {logs.map((L, i) => (
                    <div key={i} style={{ color: L.type === 'error' ? '#ef4444' : L.type==='success' ? '#22c55e' : '#94a3b8' }}>
                      [{new Date().toLocaleTimeString()}] {L.type === 'success' ? '✅' : L.type === 'error' ? '❌' : 'ℹ️'} {L.number} {L.reason ? `- ${L.reason}` : ''}
                    </div>
                  ))}
                  <div ref={logEndRef} />
               </div>
            </div>
          )}

          {/* History */}
          <div style={styles.card}>
             <h3 style={styles.cardTitle}>Recent Broadcasts</h3>
             {history.length === 0 ? <p style={{ color: '#64748b', fontSize: 13 }}>No recent broadcasts.</p> : (
               <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Name</th>
                      <th style={styles.th}>Template</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Sent / Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice(0, 5).map((h, idx) => (
                      <tr key={idx}>
                        <td style={styles.td}>{h.name}</td>
                        <td style={styles.td}>{h.templateId?.name || 'Custom Message'}</td>
                        <td style={styles.td}>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 600,
                            background: h.status === 'completed' ? 'rgba(52,211,153,0.12)' : h.status === 'cancelled' ? 'rgba(248,113,113,0.12)' : 'rgba(251,146,60,0.12)',
                            color: h.status === 'completed' ? '#34d399' : h.status === 'cancelled' ? '#f87171' : '#fb923c' }}>
                            {h.status}
                          </span>
                        </td>
                        <td style={styles.td}>{h.successfulSends} / {h.totalContacts}</td>
                      </tr>
                    ))}
                  </tbody>
               </table>
             )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '28px 32px', flex: 1, overflowY: 'auto', background: 'transparent' },
  header: { marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: '#e2e8f0', marginBottom: 4, letterSpacing: -0.3 },
  subtitle: { color: '#64748b', fontSize: 13 },
  card: { background: 'rgba(6, 12, 28, 0.9)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 12, padding: 24, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' },
  cardTitle: { fontSize: 15, fontWeight: 600, color: '#e2e8f0', marginBottom: 20 },
  field: { marginBottom: 16 },
  label: { fontSize: 11, color: '#64748b', display: 'block', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 },
  input: { width: '100%', padding: '10px 12px', background: 'rgba(2, 5, 16, 0.85)', border: '1px solid rgba(99,102,241,0.18)', borderRadius: 8, color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', outline: 'none' },
  textarea: { width: '100%', padding: '10px 12px', background: 'rgba(2, 5, 16, 0.85)', border: '1px solid rgba(99,102,241,0.18)', borderRadius: 8, color: '#e2e8f0', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', outline: 'none' },
  fileInput: { width: '100%', padding: '8px', background: 'rgba(2, 5, 16, 0.7)', border: '1px dashed rgba(99,102,241,0.3)', borderRadius: 8, color: '#94a3b8', fontSize: 13, cursor: 'pointer' },
  startBtn: { width: '100%', padding: '12px', background: 'linear-gradient(135deg, #059669, #34d399)', border: 'none', borderRadius: 8, color: 'white', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 10, boxShadow: '0 4px 18px rgba(52,211,153,0.25)', fontFamily: 'inherit' },
  progressBg: { width: '100%', height: 6, background: 'rgba(2, 5, 16, 0.8)', borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #6366f1, #818cf8)', transition: 'width 0.3s ease' },
  badge: { fontSize: 11, background: 'rgba(56,189,248,0.12)', color: '#38bdf8', padding: '4px 10px', borderRadius: 12, fontWeight: 600 },
  terminal: { background: 'rgba(0,0,0,0.6)', borderRadius: 8, padding: 12, height: 200, overflowY: 'auto', fontFamily: 'monospace', fontSize: 11, marginTop: 16, border: '1px solid rgba(99,102,241,0.15)', backdropFilter: 'blur(4px)' },
  stopBtn: { padding: '6px 14px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.35)', borderRadius: 8, color: '#f87171', fontWeight: 600, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' },
  th: { borderBottom: '1px solid rgba(99,102,241,0.12)', padding: '8px 12px', color: '#64748b', fontWeight: 600 },
  td: { borderBottom: '1px solid rgba(99,102,241,0.07)', padding: '12px', color: '#e2e8f0' },
};
